import React, { useMemo } from 'react';
import { useLiveTicks, tickLtp } from '../services/liveTicks';
import '../styles/TradeHistoryPanel.css';

// True if `ts` falls on today's local calendar date — used to split the all-time P&L summary
// below into "Today" vs "All-time" so it's never mistaken for one when it means the other
// (see the BANKNIFTY "56 all-time trades read as 56 today" incident this was added for).
const isToday = ts => {
  try {
    const d = new Date(ts);
    const now = new Date();
    return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  } catch { return false; }
};

function computeStats(trades) {
  const closed  = trades.filter(t => t.outcome !== 'OPEN');
  const wins    = closed.filter(t => t.outcome === 'WIN').length;
  const losses  = closed.filter(t => t.outcome === 'LOSS').length;
  const total   = closed.length;
  const winRate = total > 0 ? Math.round(wins / total * 100) : null;

  const byVerdict = {};
  ['TAKE TRADE', 'ON RISK'].forEach(v => {
    const g = closed.filter(t => t.finalVerdict === v);
    const w = g.filter(t => t.outcome === 'WIN').length;
    byVerdict[v] = { total: g.length, wins: w, winRate: g.length > 0 ? Math.round(w / g.length * 100) : null };
  });

  const mtfYes = closed.filter(t => t.mtfAligned);
  const mtfNo  = closed.filter(t => !t.mtfAligned);
  const mtfYesWR = mtfYes.length > 0 ? Math.round(mtfYes.filter(t => t.outcome === 'WIN').length / mtfYes.length * 100) : null;
  const mtfNoWR  = mtfNo.length  > 0 ? Math.round(mtfNo.filter(t => t.outcome  === 'WIN').length / mtfNo.length  * 100) : null;

  const DTE_CATS = ['0DTE', '1-2DTE', '3-7DTE', '8+DTE'];
  const byDte = {};
  DTE_CATS.forEach(cat => {
    const g = closed.filter(t => t.dteCategory === cat);
    const w = g.filter(t => t.outcome === 'WIN').length;
    byDte[cat] = { total: g.length, wins: w, winRate: g.length > 0 ? Math.round(w / g.length * 100) : null };
  });

  const noSr    = closed.filter(t => !t.srBlocked);
  const noSrWR  = noSr.length  > 0 ? Math.round(noSr.filter(t => t.outcome  === 'WIN').length / noSr.length  * 100) : null;
  const noPcr   = closed.filter(t => !t.pcrOpposing);
  const noPcrWR = noPcr.length > 0 ? Math.round(noPcr.filter(t => t.outcome === 'WIN').length / noPcr.length * 100) : null;

  // All-time P&L across all closed trades that have price data — spans every day ever
  // recorded, not just today, despite the summary line historically being labeled "Session".
  const pnlTrades = closed.filter(t => t.pnlPts != null);
  const totalPnlPts = pnlTrades.reduce((s, t) => s + t.pnlPts, 0);
  const totalPnlRs  = pnlTrades.reduce((s, t) => s + (t.pnlPts * (t.allocatedLotSize ?? t.lotSize ?? 50)), 0);

  // Today-only slice of the same P&L, for the "Today" row alongside "All-time" in the UI.
  // Booked on the exit day: a positional trade entered on an earlier day and closed today counts today.
  const todayPnlTrades = pnlTrades.filter(t => isToday(t.exitTime || t.timestamp));
  const todayPnlPts    = todayPnlTrades.reduce((s, t) => s + t.pnlPts, 0);
  const todayPnlRs     = todayPnlTrades.reduce((s, t) => s + (t.pnlPts * (t.allocatedLotSize ?? t.lotSize ?? 50)), 0);

  return { wins, losses, total, winRate, byVerdict, mtfYes, mtfNo, mtfYesWR, mtfNoWR,
           byDte, noSr, noSrWR, noPcr, noPcrWR, totalPnlPts, totalPnlRs, pnlTrades,
           todayPnlPts, todayPnlRs, todayPnlTrades };
}

// Badge for a trade: CE / PE for a single-leg buy, the structure code (IC, IF, BPS, BCS) for a
// backend range-day selling structure — its `structure` label starts with that code.
// Recent trades, latest activity first: open trades on top, then closed ones by exit time, so a
// positional trade closed today sits with today's trades rather than at its entry day.
const recencyKey = t => (t.outcome === 'OPEN' ? Infinity : new Date(t.exitTime || t.timestamp).getTime() || 0);

// LEVEL_PUT_SPREAD is a debit structure (pay to open, receive to close); the other strategies are credits.
const isDebitStructure = t => t.strategy === 'LEVEL_PUT_SPREAD';

const legBadge = t => (t.strategy
  ? { cls: 'th-dir-sell', text: (t.structure || t.strategy).split(' ')[0] }
  : { cls: t.dir === 'call' ? 'th-dir-call' : 'th-dir-put', text: t.dir === 'call' ? 'CE' : 'PE' });

const TradeHistoryPanel = ({
  trades = [],
  gammaData,
  onMarkOutcome,
  onSquareOff,
  onClearHistory,
  onGenerateReport,
  onGenerateWeeklyReport,
  onGenerateMonthlyReport,
  openStrategy, // backend's live view of the SYSTEM seller's open structure (system-status)
}) => {
  const pending = trades.filter(t => t.outcome === 'OPEN');
  const stats   = useMemo(() => computeStats(trades), [trades]);

  const fmtTime = ts => {
    try {
      const d = new Date(ts);
      return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return ''; }
  };
  const fmtDate = ts => {
    try {
      const d = new Date(ts);
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch { return ''; }
  };

  // Live LTP for a recorded trade's entry strike: the 1 s tick feed when fresh, else the 5 s chain.
  const ticks = useLiveTicks();
  const getLiveLTP = (trade) => {
    if (ticks && ticks.symbol === trade.symbol) {
      const t = tickLtp(ticks, trade.expiryDate, trade.entryStrike, trade.dir === 'call' ? 'CE' : trade.dir === 'put' ? 'PE' : null);
      if (t != null) return t;
    }
    if (!gammaData?.strikeData || !trade.entryStrike) return null;
    const row = gammaData.strikeData.find(s => s.strikePrice === trade.entryStrike);
    if (!row) return null;
    return trade.dir === 'call' ? row.ceLTP : row.peLTP;
  };

  const handleSquareOff = (trade) => {
    const exitLTP = getLiveLTP(trade);
    // One click here instantly closes a live position at whatever the market happens to be
    // trading at — no undo. Confirm before firing, same as GreekStopLossPanel's own Square
    // Off button (both act on this same trade once it's recorded).
    const dirLabel = trade.dir === 'call' ? 'CALL' : 'PUT';
    const confirmMsg = `Square off ${trade.symbol} ${dirLabel} ${trade.entryStrike ?? ''} now at `
      + `${exitLTP != null ? `₹${exitLTP.toFixed(2)}` : 'unknown LTP'}?`;
    if (!window.confirm(confirmMsg)) return;
    onSquareOff(trade, exitLTP, 'Manual Square Off');
  };

  const WinBar = ({ winRate, total }) => {
    if (winRate == null || total === 0) return <span className="th-filter-na">—</span>;
    const color = winRate >= 60 ? '#00c853' : winRate >= 45 ? '#ff9800' : '#d32f2f';
    return (
      <div className="th-filter-bar-wrap">
        <div className="th-filter-bar-track">
          <div className="th-filter-bar-fill" style={{ width: `${winRate}%`, background: color }} />
        </div>
        <span className="th-filter-pct" style={{ color }}>{winRate}%</span>
      </div>
    );
  };

  return (
    <div className="th-panel">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="th-header">
        <span className="th-title">Win Rate Tracker</span>
        <div className="th-header-right">
          {stats.winRate != null && (
            <span className="th-overall-wr" style={{ color: stats.winRate >= 50 ? '#00c853' : '#d32f2f' }}>
              {stats.winRate}% · {stats.wins}W/{stats.losses}L
            </span>
          )}
          {trades.length > 0 && (
            <button className="th-report-btn" onClick={onGenerateReport} title="Download today's EOD report">EOD</button>
          )}
          {trades.length > 0 && (
            <button className="th-report-btn th-report-btn-weekly" onClick={onGenerateWeeklyReport} title="Download this week's journal & P&L report">Weekly</button>
          )}
          {trades.length > 0 && (
            <button className="th-report-btn th-report-btn-monthly" onClick={onGenerateMonthlyReport} title="Download this month's journal & P&L report">Monthly</button>
          )}
          {trades.length > 0 && (
            <button className="th-clear-btn" onClick={onClearHistory}>Clear</button>
          )}
        </div>
      </div>

      {/* ── P&L summary — Today + All-time (never conflate the two: see BANKNIFTY
          "56 all-time trades read as 56 today" incident) ──────────────────── */}
      {stats.todayPnlTrades.length > 0 && (
        <div className="th-pnl-summary">
          <span className="th-pnl-label">Today P&L</span>
          <span className={`th-pnl-pts ${stats.todayPnlPts >= 0 ? 'th-pnl-positive' : 'th-pnl-negative'}`}>
            {stats.todayPnlPts >= 0 ? '+' : ''}{stats.todayPnlPts.toFixed(1)} pts
          </span>
          <span className={`th-pnl-rs ${stats.todayPnlRs >= 0 ? 'th-pnl-positive' : 'th-pnl-negative'}`}>
            {stats.todayPnlRs >= 0 ? '+' : ''}₹{Math.round(stats.todayPnlRs).toLocaleString('en-IN')}
          </span>
          <span className="th-pnl-count">{stats.todayPnlTrades.length} trade{stats.todayPnlTrades.length > 1 ? 's' : ''}</span>
        </div>
      )}
      {stats.pnlTrades.length > 0 && (
        <div className="th-pnl-summary">
          <span className="th-pnl-label">All-time P&L</span>
          <span className={`th-pnl-pts ${stats.totalPnlPts >= 0 ? 'th-pnl-positive' : 'th-pnl-negative'}`}>
            {stats.totalPnlPts >= 0 ? '+' : ''}{stats.totalPnlPts.toFixed(1)} pts
          </span>
          <span className={`th-pnl-rs ${stats.totalPnlRs >= 0 ? 'th-pnl-positive' : 'th-pnl-negative'}`}>
            {stats.totalPnlRs >= 0 ? '+' : ''}₹{Math.round(stats.totalPnlRs).toLocaleString('en-IN')}
          </span>
          <span className="th-pnl-count">{stats.pnlTrades.length} trade{stats.pnlTrades.length > 1 ? 's' : ''}</span>
        </div>
      )}

      {/* ── Pending: square off or mark manually ─────────────────────────── */}
      {pending.length > 0 && (
        <div className="th-pending-section">
          <div className="th-section-label">
            Open positions
            {ticks?.spot != null && (
              <span className="th-live-spot" title="Kite tick, refreshed every second">
                {' '}· {ticks.symbol} {ticks.spot.toFixed(2)} live
              </span>
            )}
          </div>
          {pending.map(t => {
            const liveLTP  = getLiveLTP(t);
            const livePnl  = (liveLTP != null && t.entryLTP != null) ? liveLTP - t.entryLTP : null;
            const livePnlRs = livePnl != null ? Math.round(livePnl * (t.allocatedLotSize ?? t.lotSize ?? 50)) : null;
            return (
              <div key={t.id} className="th-pending-row">
                {/* Top line: direction + symbol + signal + time */}
                <div className="th-pending-meta">
                  <span className={`th-dir-badge ${legBadge(t).cls}`} title={t.structure}>
                    {legBadge(t).text}
                  </span>
                  <span className="th-pending-symbol">{t.symbol}</span>
                  {t.strategy
                    ? <span className="th-pending-strike">{t.structure}</span>
                    : t.entryStrike && <span className="th-pending-strike">{t.entryStrike}</span>}
                  <span className="th-pending-fv" style={{ color: t.finalVerdict === 'TAKE TRADE' ? '#00c853' : '#e65100' }}>
                    {t.finalVerdict}
                  </span>
                  {t.isManual && <span className="th-flag th-flag-manual" title="Manually entered trade">MANUAL</span>}
                  <span className="th-pending-time">{fmtTime(t.timestamp)}</span>
                  {t.dteCategory && <span className="th-pending-dte">{t.dteCategory}</span>}
                </div>

                {/* LTP row: entry → live → unrealised P&L (a selling structure shows its credit) */}
                {t.strategy ? (() => {
                  // Live marks come from the backend (priced on the seller's own expiry chain,
                  // which may differ from this dashboard's); the stored legs are the fallback.
                  const live = openStrategy && String(openStrategy.clientId) === String(t.id) ? openStrategy : null;
                  const legs = live?.legDetails ?? t.legs ?? [];
                  const mark = live?.markDebit;
                  const pts  = mark != null && t.entryLTP != null ? t.entryLTP - mark : null;
                  const rs   = live?.unrealisedRs;
                  return (
                  <>
                  <div className="th-pending-ltp">
                    <span className="th-ltp-entry">Credit ₹{t.entryLTP?.toFixed(2)}</span>
                    {mark != null && (
                      <>
                        <span className="th-ltp-arrow">→</span>
                        <span className="th-ltp-live" title="Net debit to close all legs now (mid prices)">Live ₹{mark.toFixed(2)}</span>
                      </>
                    )}
                    {pts != null && (
                      <span className={`th-ltp-pnl ${pts >= 0 ? 'th-pnl-positive' : 'th-pnl-negative'}`}>
                        ({pts >= 0 ? '+' : ''}{pts.toFixed(1)} pts{rs != null && <> · {rs >= 0 ? '+' : '−'}₹{Math.abs(rs).toLocaleString('en-IN')}</>})
                      </span>
                    )}
                    {live && mark == null && <span className="th-ltp-na">Live price not available this poll</span>}
                  </div>
                  {legs.length > 0 && (
                    <div className="th-legs">
                      {legs.map((l, i) => {
                        const sell = l.side === 'SELL';
                        const legPts = l.markPx != null && l.entryPx != null
                          ? (sell ? l.entryPx - l.markPx : l.markPx - l.entryPx) : null;
                        return (
                          <div key={i} className="th-leg-row">
                            <span className={`th-leg-side ${sell ? 'th-leg-sell' : 'th-leg-buy'}`}>{sell ? 'SELL' : 'BUY'}</span>
                            <span className="th-leg-strike">{l.strike} {l.right}</span>
                            <span className="th-ltp-entry">₹{l.entryPx?.toFixed(2)}</span>
                            {l.markPx != null && (
                              <>
                                <span className="th-ltp-arrow">→</span>
                                <span className="th-ltp-live">₹{l.markPx.toFixed(2)}</span>
                              </>
                            )}
                            {legPts != null && (
                              <span className={`th-ltp-pnl ${legPts >= 0 ? 'th-pnl-positive' : 'th-pnl-negative'}`}>
                                {legPts >= 0 ? '+' : ''}{legPts.toFixed(1)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="th-pending-ltp">
                    {live?.targetDebit != null && live?.stopDebit != null && (
                      <span className="th-managed-note">
                        target: debit ≤ ₹{live.targetDebit.toFixed(2)} · stop: debit ≥ ₹{live.stopDebit.toFixed(2)}
                        {live.expiry ? ` · expiry ${live.expiry}` : ''}
                      </span>
                    )}
                    <span className="th-managed-note">managed by the backend — target / stop / range breakout / 15:15</span>
                  </div>
                  </>
                  );
                })() : (
                <div className="th-pending-ltp">
                  {t.entryLTP != null && (
                    <span className="th-ltp-entry">Entry ₹{t.entryLTP.toFixed(1)}</span>
                  )}
                  {liveLTP != null && (
                    <>
                      <span className="th-ltp-arrow">→</span>
                      <span className="th-ltp-live">Live ₹{liveLTP.toFixed(1)}</span>
                    </>
                  )}
                  {livePnl != null && (
                    <span className={`th-ltp-pnl ${livePnl >= 0 ? 'th-pnl-positive' : 'th-pnl-negative'}`}>
                      ({livePnl >= 0 ? '+' : ''}{livePnl.toFixed(1)} pts · {livePnlRs >= 0 ? '+' : ''}₹{Math.abs(livePnlRs).toLocaleString('en-IN')})
                    </span>
                  )}
                  {t.entryLTP == null && <span className="th-ltp-na">No entry LTP captured</span>}
                </div>
                )}

                {/* Action buttons — not for a multi-leg structure (one strike's LTP can't price it) */}
                {!t.strategy && (
                <div className="th-pending-btns">
                  <button className="th-sq-btn" onClick={() => handleSquareOff(t)}
                    title={liveLTP != null ? `Exit at ₹${liveLTP.toFixed(1)}` : 'Current LTP not available'}>
                    Square Off
                  </button>
                  <button className="th-win-btn"  onClick={() => onMarkOutcome(t.id, 'WIN')}>Win</button>
                  <button className="th-loss-btn" onClick={() => onMarkOutcome(t.id, 'LOSS')}>Loss</button>
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Filter Effectiveness ──────────────────────────────────────────── */}
      {stats.total >= 3 && (
        <div className="th-stats-section">
          <div className="th-section-label">Filter effectiveness · {stats.total} closed</div>

          <div className="th-filter-group">
            <div className="th-filter-group-title">Signal Strength</div>
            {['TAKE TRADE', 'ON RISK'].map(v => {
              const s = stats.byVerdict[v];
              if (!s || s.total === 0) return null;
              return (
                <div key={v} className="th-filter-row">
                  <span className="th-filter-key" style={{ color: v === 'TAKE TRADE' ? '#00c853' : '#e65100' }}>{v}</span>
                  <span className="th-filter-count">{s.wins}W/{s.total - s.wins}L</span>
                  <WinBar winRate={s.winRate} total={s.total} />
                </div>
              );
            })}
          </div>

          {(stats.mtfYes.length > 0 || stats.mtfNo.length > 0) && (
            <div className="th-filter-group">
              <div className="th-filter-group-title">MTF Alignment</div>
              {[
                { key: 'All TFs aligned',   list: stats.mtfYes, wr: stats.mtfYesWR },
                { key: 'TF conflict / n/a', list: stats.mtfNo,  wr: stats.mtfNoWR  },
              ].filter(r => r.list.length > 0).map(r => (
                <div key={r.key} className="th-filter-row">
                  <span className="th-filter-key">{r.key}</span>
                  <span className="th-filter-count">
                    {r.list.filter(t => t.outcome === 'WIN').length}W / {r.list.filter(t => t.outcome === 'LOSS').length}L
                  </span>
                  <WinBar winRate={r.wr} total={r.list.length} />
                </div>
              ))}
            </div>
          )}

          {Object.values(stats.byDte).some(s => s.total > 0) && (
            <div className="th-filter-group">
              <div className="th-filter-group-title">DTE Category</div>
              {Object.entries(stats.byDte).filter(([, s]) => s.total > 0).map(([cat, s]) => (
                <div key={cat} className="th-filter-row">
                  <span className="th-filter-key">{cat}</span>
                  <span className="th-filter-count">{s.wins}W/{s.total - s.wins}L</span>
                  <WinBar winRate={s.winRate} total={s.total} />
                </div>
              ))}
            </div>
          )}

          {(stats.noSr.length > 0 || stats.noPcr.length > 0) && (
            <div className="th-filter-group">
              <div className="th-filter-group-title">Gate Filters (no-block only)</div>
              {stats.noSr.length > 0 && (
                <div className="th-filter-row">
                  <span className="th-filter-key">S/R not blocking</span>
                  <span className="th-filter-count">
                    {stats.noSr.filter(t => t.outcome === 'WIN').length}W/{stats.noSr.filter(t => t.outcome === 'LOSS').length}L
                  </span>
                  <WinBar winRate={stats.noSrWR} total={stats.noSr.length} />
                </div>
              )}
              {stats.noPcr.length > 0 && (
                <div className="th-filter-row">
                  <span className="th-filter-key">PCR not opposing</span>
                  <span className="th-filter-count">
                    {stats.noPcr.filter(t => t.outcome === 'WIN').length}W/{stats.noPcr.filter(t => t.outcome === 'LOSS').length}L
                  </span>
                  <WinBar winRate={stats.noPcrWR} total={stats.noPcr.length} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Recent trades list ────────────────────────────────────────────── */}
      {trades.length > 0 && (
        <div className="th-history-section">
          <div className="th-section-label">Recent trades (last 20)</div>
          <div className="th-history-list">
            {[...trades].reverse().sort((a, b) => recencyKey(b) - recencyKey(a)).slice(0, 20).map(t => {
              const pnlColor = t.pnlPts == null ? null : t.pnlPts >= 0 ? '#2e7d32' : '#c62828';
              return (
                <div key={t.id} className={`th-trade-row ${
                  t.outcome === 'WIN'  ? 'th-trade-win'  :
                  t.outcome === 'LOSS' ? 'th-trade-loss' : 'th-trade-open'
                }`}>
                  <span className={`th-trade-oc ${
                    t.outcome === 'WIN'  ? 'th-oc-win'  :
                    t.outcome === 'LOSS' ? 'th-oc-loss' : 'th-oc-open'
                  }`}>
                    {t.outcome === 'WIN' ? 'W' : t.outcome === 'LOSS' ? 'L' : '?'}
                  </span>
                  <span className={`th-trade-dir ${legBadge(t).cls}`} title={t.structure}>
                    {legBadge(t).text}
                  </span>
                  <span className="th-trade-symbol">{t.symbol}</span>
                  {!t.strategy && t.entryStrike && <span className="th-trade-strike">{t.entryStrike}</span>}
                  <span className="th-trade-fv" style={{
                    color: t.finalVerdict === 'TAKE TRADE' ? '#00c853' :
                           t.finalVerdict === 'ON RISK'    ? '#e65100' :
                           t.finalVerdict === 'EMA TREND'  ? '#1e88e5' : '#999',
                  }} title={t.finalVerdict || t.strategy || undefined}>
                    {t.finalVerdict === 'TAKE TRADE' ? 'TT' : t.finalVerdict === 'ON RISK' ? 'OR'
                      : t.finalVerdict === 'EMA TREND' ? 'EMA' : t.isManual ? 'MT'
                      : t.strategy === 'WEEKLY_CONDOR' ? 'WC' : t.strategy === 'LEVEL_PUT_SPREAD' ? 'LS' : t.strategy ? 'RS' : '—'}
                  </span>
                  {/* LTP prices */}
                  {t.entryLTP != null && (
                    <span className="th-trade-ltps" title={!t.strategy ? undefined
                      : isDebitStructure(t) ? 'net debit paid → net credit to close' : 'net credit → net debit to close'}>
                      {t.strategy ? (isDebitStructure(t) ? 'Db ' : 'Cr ') : ''}₹{t.entryLTP.toFixed(1)}
                      {t.exitLTP != null && <> → {t.strategy ? (isDebitStructure(t) ? 'Cr ' : 'Db ') : ''}₹{t.exitLTP.toFixed(1)}</>}
                    </span>
                  )}
                  {/* P&L */}
                  {t.pnlPts != null && (
                    <span className="th-trade-pnl" style={{ color: pnlColor }}>
                      {t.pnlPts >= 0 ? '+' : ''}{t.pnlPts.toFixed(1)}
                    </span>
                  )}
                  <span className="th-trade-flags">
                    {t.isManual    && <span className="th-flag th-flag-manual" title="Manually entered trade">MANUAL</span>}
                    {t.mtfAligned  && <span className="th-flag th-flag-mtf" title="MTF aligned">MTF</span>}
                    {t.srBlocked   && <span className="th-flag th-flag-sr"  title="S/R blocked">S/R</span>}
                    {t.pcrOpposing && <span className="th-flag th-flag-pcr" title="PCR opposing">PCR</span>}
                  </span>
                  <span className="th-trade-time">
                    {fmtDate(t.timestamp)} {fmtTime(t.timestamp)}
                    {t.exitTime && fmtDate(t.exitTime) !== fmtDate(t.timestamp)
                      && <> → {fmtDate(t.exitTime)} {fmtTime(t.exitTime)}</>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {trades.length === 0 && (
        <div className="th-empty">
          <div className="th-empty-icon">📋</div>
          <div className="th-empty-text">No trades recorded yet</div>
          <div className="th-empty-sub">
            Trades are captured automatically. When a TAKE TRADE or ON RISK signal persists
            for 2 consecutive polls, it is recorded here and saved to the server.
            Start the analyzer and wait for a signal — no manual action needed.
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeHistoryPanel;
